<!--
  OPDS Breadcrumbs Navigation Component
  Shows navigation path and provides quick navigation buttons
-->
<script lang="ts">
  import { opdsStore } from '../stores/opds.svelte';

  const { currentFeed, navigationHistory } = $derived(opdsStore);

  async function navigateToItem(index: number) {
    // Navigate back to specific history item
    const targetItem = navigationHistory[index];
    if (!targetItem) return;

    // Remove all items after target
    while (navigationHistory.length > index + 1) {
      navigationHistory.pop();
    }

    await opdsStore.fetchFeed(targetItem.url, targetItem.title);
  }
</script>

{#if navigationHistory.length > 0}
  <div class="breadcrumbs bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
    <div class="flex items-center gap-2 text-sm">
      <!-- Home button -->
      <button
        on:click={() => opdsStore.navigateHome()}
        class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100
               transition-colors flex items-center gap-1"
        title="Back to catalogs"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
        <span class="hidden sm:inline">Home</span>
      </button>

      <!-- Breadcrumb trail -->
      {#each navigationHistory as item, index}
        <span class="text-gray-400 dark:text-gray-600">/</span>

        {#if index === navigationHistory.length - 1}
          <!-- Current page (not clickable) -->
          <span class="text-gray-900 dark:text-gray-100 font-medium truncate max-w-xs">
            {item.title}
          </span>
        {:else}
          <!-- Previous pages (clickable) -->
          <button
            on:click={() => navigateToItem(index)}
            class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300
                   hover:underline transition-colors truncate max-w-xs"
          >
            {item.title}
          </button>
        {/if}
      {/each}
    </div>

    <!-- Navigation controls -->
    <div class="flex items-center gap-2 mt-2">
      {#if currentFeed?.upLink}
        <button
          on:click={() => opdsStore.navigateUp()}
          class="px-3 py-1 text-xs rounded-md bg-white dark:bg-gray-700
                 border border-gray-300 dark:border-gray-600
                 text-gray-700 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors
                 flex items-center gap-1"
          title="Go up one level"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          Up
        </button>
      {/if}

      {#if currentFeed?.startLink && navigationHistory.length > 1}
        <button
          on:click={() => opdsStore.navigateToStart()}
          class="px-3 py-1 text-xs rounded-md bg-white dark:bg-gray-700
                 border border-gray-300 dark:border-gray-600
                 text-gray-700 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors
                 flex items-center gap-1"
          title="Go to catalog root"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          Root
        </button>
      {/if}

      {#if navigationHistory.length > 1}
        <button
          on:click={() => opdsStore.navigateBack()}
          class="px-3 py-1 text-xs rounded-md bg-white dark:bg-gray-700
                 border border-gray-300 dark:border-gray-600
                 text-gray-700 dark:text-gray-300
                 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors
                 flex items-center gap-1"
          title="Go back"
        >
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
      {/if}
    </div>
  </div>
{/if}

<style>
  .breadcrumbs {
    max-width: 100%;
    overflow-x: auto;
  }

  .truncate {
    display: inline-block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
